/**
 * Chart series colours — the single documented source for non-tenant-branded chart
 * colour (SIMS-8458 AC6).
 *
 * These are deliberately NOT theme roles and NOT tenant-configurable. Charts are not
 * tenant-branded in the CMI Design System, so the series colours are fixed across
 * tenants; Alexander Yong confirmed this on SIMS-7504 (comment 65008): "the chart-series
 * colours are not going into the colour settings configuration ... the chart series stay
 * fixed."
 *
 * AC6 on SIMS-8458 is the authoritative wording where it differs from the blanket
 * "no hardcoded hex" phrasing elsewhere on the Epic:
 *
 *   "no colour is written as an inline hex literal inside a component, and
 *    tenant-branded colours resolve from the active theme, and the non-tenant-branded
 *    chart series resolve from a single documented named source — referencing that named
 *    source satisfies this criterion."
 *
 * So the literals live here, once, and components reference them by name. The mechanism
 * (module vs theme entry) was left to the implementation team; a module keeps chart
 * colour out of the tenant theme, which is what "not tenant-branded" means in practice.
 *
 * Values are the design's own, from the authoring source in the Epic attachment
 * (`incentivesChart`: `const greenC = '#4C9E82', purpleC = '#B39DDB'`). Each is a muted
 * sibling of a brand colour — the green sits beside the brand-primary teal, the purple is
 * a pale tertiary — so the pair reads as brand-adjacent without being tenant-specific.
 *
 * Both the bars and their legend swatches must use these values (SIMS-7548 AC2); with
 * Recharts the legend derives its swatch from the series `fill`, so referencing the
 * constant once per series covers both.
 */
export const CHART_SERIES = {
	/** "Earned" bars — incentives actually earned. */
	earned: "#4C9E82",
	/** "Potential" bars — incentives available but not yet earned. */
	potential: "#B39DDB",
} as const;

/**
 * The withdrawal-score progression line.
 *
 * Unlike the series pair above this one IS tenant-branded: the design draws it in
 * `--brand-tertiary-main`, which the app already carries as the `tertiaryColor` theme
 * role (SIMS-7505). Callers should pass `theme.palette.tertiary.main` rather than a
 * constant — this note exists so the next reader does not add a literal here for
 * symmetry.
 */
export const CHART_LINE_USES_THEME_TERTIARY = true;
